import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './ClassificationWall.scss';
import { useClassificationPosts } from '../../hooks/useClassificationPosts';
import { Loader } from '@/core/components/Loader/Loader';
import { useToast } from '@/core/components/ToastProvider/ToastProvider';
import { CLASSIFICATION_EVENTS } from '../../constants/events';
import { createRefreshDataHandler } from '@/core/utils/createRefreshDataHandler';
import { ClassifySortBy } from '@/core/components/SortBy/ClassifySortBy/ClassifySortby';
import { Pagination } from '@/core/components/Pagination/Pagination';
import { ClassificationCardList, ClassificationCardListStateType } from '../../components/ClassificationCardList/ClassificationCardList';
import { BdsButton } from '@/core/constants/webComponents';
import { CreateFileCard, CreateFileData } from '../../components/CreateFileCard/CreateFileCard';
import { MultipleEditionModal } from '../../components/MultipleEditionModal/MultipleEditionModal';
import { MultipleEditionProvider, useMultipleEdition } from '../../contexts/MultipleEditionContext';

export interface ClassificationWallProps {
  onRetry?: () => void;
}

export const ClassificationWall: React.FC<ClassificationWallProps> = (props) => (
  <MultipleEditionProvider>
    <ClassificationWallInner {...props} />
  </MultipleEditionProvider>
);

const ClassificationWallInner: React.FC<ClassificationWallProps> = ({
  onRetry = () => globalThis.location.reload(),
}) => {
  const { t } = useTranslation();
  const { posts, totalCount, hasTimedOut, hasError } = useClassificationPosts();
  const { showToast } = useToast();
  const hasDispatchedLoadedRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const [externalHasError, setExternalHasError] = useState(false);
  const [hasPostsCounted, setHasPostsCounted] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isCreateFileOpen, setIsCreateFileOpen] = useState(false);
  const { isMultipleEditionOpen, openMultipleEdition, closeMultipleEdition, setAvailablePostIds, registerPostsMeta, setTotalFilteredPostCount, isSelectionLocked } = useMultipleEdition();
  const totalItems = totalCount ?? posts.length;

  useEffect(() => {
    if (isSelectionLocked) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isSelectionLocked]);

  useEffect(() => {
    setTotalFilteredPostCount(totalItems);
  }, [totalItems, setTotalFilteredPostCount]);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    globalThis.addEventListener('online', handleOnline);
    globalThis.addEventListener('offline', handleOffline);

    return () => {
      globalThis.removeEventListener('online', handleOnline);
      globalThis.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!isOffline) return;

    const retryTimeout = setTimeout(() => {
      onRetry();
    }, 30_000);

    return () => clearTimeout(retryTimeout);
  }, [isOffline, onRetry]);

  const stateType = useMemo((): ClassificationCardListStateType => {
    if (isOffline) return 'offline';
    if (externalHasError || hasError) return 'error';
    if (hasTimedOut) return 'loadingTime';
    return 'noResults';
  }, [isOffline, hasError, hasTimedOut, externalHasError]);

  const hasEmptyState = isOffline || hasPostsCounted || externalHasError || hasError || hasTimedOut;
  const shouldShowPosts = posts.length > 0 && !isOffline && !externalHasError;
  const shouldShowEmptyState = !isLoading && hasEmptyState && !shouldShowPosts;
  const shouldShowFab = !isLoading && shouldShowPosts && !isMultipleEditionOpen;
  const shouldShowPostsSection = !isLoading && shouldShowPosts;

  const makeToastHandler = useCallback(
    (variant: 'success' | 'warning' | 'error', titleKey: string, textKey: string, icon: string) =>
      () => {
        showToast({ variant, title: t(titleKey), text: t(textKey) ?? '', icon }).catch((err: unknown) => {
          console.error('[ClassificationWall] Failed to show toast:', err);
        });
      },
    [showToast, t],
  );

  useEffect(() => {
    const handleLoading = (event: Event) => {
      const customEvent = event as CustomEvent<{ isLoading: boolean }>;
      if (customEvent.detail && typeof customEvent.detail.isLoading === 'boolean') {
        setIsLoading(customEvent.detail.isLoading);
      }
    };

    const handleErrorCountPosts       = makeToastHandler('warning', 'classifyNotifications.countPostsError', '', 'error');
    const handleSuccessCountPosts     = makeToastHandler('success', 'classifyNotifications.countPostsSuccess', '', 'checkball');
    const handleAnnotationSuccess     = makeToastHandler('success', 'classifyNotifications.annotationSuccess', '', 'checkball');
    const handleAnnotationError       = makeToastHandler('warning', 'classifyNotifications.annotationError', '', 'error');
    const handleCopySuccess           = makeToastHandler('success', 'classifyNotifications.copySuccess', '', 'checkball');
    const handleCopyError             = makeToastHandler('warning', 'classifyNotifications.copyError', '', 'error');
    const handleCopyIdSuccess         = makeToastHandler('success', 'classifyNotifications.copyIdSuccess', '', 'checkball');
    const handleCopyIdError           = makeToastHandler('warning', 'classifyNotifications.copyIdError', '', 'error');
    const handleRefreshSuccess        = makeToastHandler('success', 'classifyNotifications.refreshSuccess', '', 'checkball');
    const handleRefreshData           = createRefreshDataHandler(
      CLASSIFICATION_EVENTS.NOTIFY_REFRESH_SUCCESS,
      CLASSIFICATION_EVENTS.NOTIFY_REFRESH_ERROR,
    );
    const handleRefreshError          = makeToastHandler('warning', 'classifyNotifications.refreshError', '', 'error');
    const handleBanUserLoading        = makeToastHandler('warning', 'classifyNotifications.banUserLoading', '', 'error');
    const handleBanUserSuccess        = makeToastHandler('success', 'classifyNotifications.banUserSuccess', 'classifyNotifications.banUserSuccessText', 'checkball');
    const handleBanUserIdError        = makeToastHandler('warning', 'classifyNotifications.banUserIdError', '', 'error');
    const handleRemoveSuccess = (event: Event) => {
      const { spam } = (event as CustomEvent).detail ?? {};
      const titleKey = spam
        ? 'classifyNotifications.restorePostSuccess'
        : 'classifyNotifications.removeFromSearchSuccess';
      showToast({ variant: 'success', title: t(titleKey), text: '', icon: 'checkball' }).catch((err: unknown) => {
        console.error('[ClassificationWall] Failed to show toast:', err);
      });
    };
    const handleRemoveError = (event: Event) => {
      const { spam } = (event as CustomEvent).detail ?? {};
      const titleKey = spam
        ? 'classifyNotifications.restorePostError'
        : 'classifyNotifications.removeFromSearchError';
      showToast({ variant: 'warning', title: t(titleKey), text: '', icon: 'error' }).catch((err: unknown) => {
        console.error('[ClassificationWall] Failed to show toast:', err);
      });
    };
    const handleReplySuccess          = makeToastHandler('success', 'classifyNotifications.replySuccess', '', 'checkball');
    const handleReplyError            = makeToastHandler('warning', 'classifyNotifications.replyError', '', 'error');
    const handleBulkFilterSuccess         = makeToastHandler('success', 'classifyNotifications.bulkFilterAnnotateSuccess', 'classifyNotifications.bulkFilterAnnotateSuccessText', 'checkball');
    const handleBulkCommunicationError    = makeToastHandler('warning', 'classifyNotifications.bulkCommunicationError', 'classifyNotifications.bulkCommunicationErrorText', 'error');
    const handleBulkSomeAnnotationError   = makeToastHandler('warning', 'classifyNotifications.bulkSomeAnnotationError', '', 'error');
    const handleBulkClassificationError   = makeToastHandler('warning', 'classifyNotifications.bulkClassificationError', '', 'error');
    const handleBulkSelectedSuccess = (event: Event) => {
      const { count } = (event as CustomEvent).detail ?? {};
      showToast({
        variant: 'success',
        title: t('classifyNotifications.bulkSelectedAnnotateSuccess', { count }),
        text: '',
        icon: 'checkball',
      }).catch((err: unknown) => {
        console.error('[ClassificationWall] Failed to show toast:', err);
      });
    };

    document.addEventListener(CLASSIFICATION_EVENTS.LOADING,                                handleLoading);
    document.addEventListener(CLASSIFICATION_EVENTS.ERROR_COUNT_POSTS,                      handleErrorCountPosts);
    document.addEventListener(CLASSIFICATION_EVENTS.SUCCESS_COUNT_POSTS,                    handleSuccessCountPosts);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_ANNOTATION_SUCCESS,              handleAnnotationSuccess);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_ANNOTATION_ERROR,                handleAnnotationError);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_COPY_SUCCESS,                    handleCopySuccess);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_COPY_ERROR,                      handleCopyError);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_COPY_ID_SUCCESS,                 handleCopyIdSuccess);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_COPY_ID_ERROR,                   handleCopyIdError);
    document.addEventListener(CLASSIFICATION_EVENTS.REFRESH_DATA,                           handleRefreshData);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_REFRESH_SUCCESS,                 handleRefreshSuccess);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_REFRESH_ERROR,                   handleRefreshError);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_BAN_USER_LOADING,                handleBanUserLoading);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_BAN_USER_SUCCESS,                handleBanUserSuccess);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_BAN_USER_ID_ERROR,               handleBanUserIdError);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_REMOVE_FROM_SEARCH_SUCCESS,      handleRemoveSuccess);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_REMOVE_FROM_SEARCH_ERROR,        handleRemoveError);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_REPLY_SUCCESS,                   handleReplySuccess);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_REPLY_ERROR,                     handleReplyError);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_BULK_FILTER_ANNOTATE_SUCCESS,    handleBulkFilterSuccess);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_BULK_SELECTED_ANNOTATE_SUCCESS,  handleBulkSelectedSuccess);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_BULK_COMMUNICATION_ERROR,        handleBulkCommunicationError);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_BULK_SOME_ANNOTATION_ERROR,      handleBulkSomeAnnotationError);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_BULK_CLASSIFICATION_ERROR,       handleBulkClassificationError);
    const handlePostsCounted = () => setHasPostsCounted(true);
    document.addEventListener(CLASSIFICATION_EVENTS.POSTS_COUNTED, handlePostsCounted);
    const handleExternalError = () => setExternalHasError(true);
    document.addEventListener(CLASSIFICATION_EVENTS.ERROR_LOADING, handleExternalError);

    // Signal warroom that the wall is mounted (only once)
    if (!hasDispatchedLoadedRef.current) {
      hasDispatchedLoadedRef.current = true;
      document.dispatchEvent(
        new CustomEvent(CLASSIFICATION_EVENTS.LOADED, { bubbles: true, composed: true }),
      );
    }

    return () => {
      document.removeEventListener(CLASSIFICATION_EVENTS.LOADING,                                handleLoading);
      document.removeEventListener(CLASSIFICATION_EVENTS.ERROR_COUNT_POSTS,                      handleErrorCountPosts);
      document.removeEventListener(CLASSIFICATION_EVENTS.SUCCESS_COUNT_POSTS,                    handleSuccessCountPosts);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_ANNOTATION_SUCCESS,              handleAnnotationSuccess);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_ANNOTATION_ERROR,                handleAnnotationError);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_COPY_SUCCESS,                    handleCopySuccess);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_COPY_ERROR,                      handleCopyError);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_COPY_ID_SUCCESS,                 handleCopyIdSuccess);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_COPY_ID_ERROR,                   handleCopyIdError);
      document.removeEventListener(CLASSIFICATION_EVENTS.REFRESH_DATA,                           handleRefreshData);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_REFRESH_SUCCESS,                 handleRefreshSuccess);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_REFRESH_ERROR,                   handleRefreshError);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_BAN_USER_LOADING,                handleBanUserLoading);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_BAN_USER_SUCCESS,                handleBanUserSuccess);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_BAN_USER_ID_ERROR,               handleBanUserIdError);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_REMOVE_FROM_SEARCH_SUCCESS,      handleRemoveSuccess);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_REMOVE_FROM_SEARCH_ERROR,        handleRemoveError);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_REPLY_SUCCESS,                   handleReplySuccess);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_REPLY_ERROR,                     handleReplyError);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_BULK_FILTER_ANNOTATE_SUCCESS,    handleBulkFilterSuccess);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_BULK_SELECTED_ANNOTATE_SUCCESS,  handleBulkSelectedSuccess);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_BULK_COMMUNICATION_ERROR,        handleBulkCommunicationError);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_BULK_SOME_ANNOTATION_ERROR,      handleBulkSomeAnnotationError);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_BULK_CLASSIFICATION_ERROR,       handleBulkClassificationError);
      document.removeEventListener(CLASSIFICATION_EVENTS.POSTS_COUNTED, handlePostsCounted);
      document.removeEventListener(CLASSIFICATION_EVENTS.ERROR_LOADING, handleExternalError);
    };
  }, [makeToastHandler, showToast, t]);

  // ── File creation ─────────────────────────────────────────────
  const handleCreateFile = useCallback((data: CreateFileData) => {
    document.dispatchEvent(
      new CustomEvent(CLASSIFICATION_EVENTS.CREATE_FILE, {
        bubbles: true,
        composed: true,
        detail: {
          fileName: data.fileName,
          postsCount: data.postsCount,
        },
      }),
    );
    setIsCreateFileOpen(false);
  }, []);

  // Listen for Warroom success / error toasts for file creation
  useEffect(() => {
    const handleSuccess = () => {
      showToast({
        variant: 'success',
        title: t('file.createSuccessTitle'),
        text: t('file.createSuccessText'),
        icon: 'check',
      });
    };

    const handleError = () => {
      showToast({
        variant: 'error',
        title: t('file.createErrorTitle'),
        text: t('file.createErrorText'),
        icon: 'error',
      });
    };

    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_CREATE_FILE_SUCCESS, handleSuccess);
    document.addEventListener(CLASSIFICATION_EVENTS.NOTIFY_CREATE_FILE_ERROR, handleError);

    return () => {
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_CREATE_FILE_SUCCESS, handleSuccess);
      document.removeEventListener(CLASSIFICATION_EVENTS.NOTIFY_CREATE_FILE_ERROR, handleError);
    };
  }, [showToast, t]);

  useEffect(() => {
    const handlePaginationChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        const { page, limit } = customEvent.detail;
        setCurrentPage(page);
        setItemsPerPage(limit);
      }
    };

    globalThis.addEventListener('mfe-classify-pagination-change', handlePaginationChange);
    return () => globalThis.removeEventListener('mfe-classify-pagination-change', handlePaginationChange);
  }, []);

  useEffect(() => {
    const ids = posts.map((p) => p.pid ?? p._id);
    setAvailablePostIds(ids);
    registerPostsMeta(posts.map((p) => ({
      id: p.pid ?? p._id,
      channel: p.channel,
      uid: p.uid ?? '',
      polarity: p.polarity,
      gender: p.gender,
      checked: p.checked,
      spam: p.spam,
    })));
  }, [posts, setAvailablePostIds, registerPostsMeta]);

  return (
    <div className={`classification-wall${isMultipleEditionOpen ? ' classification-wall--modal-open' : ''}`}>
      <h1 className="classification-wall__title">{t('menu.classificationWallTitle')}</h1>
      <ClassifySortBy />
      {isLoading && (
        <Loader />
      )}
      {shouldShowPostsSection && (
        <>
          <Pagination
            totalItems={totalItems}
            initialPage={currentPage}
            initialLimit={itemsPerPage}
            eventName="mfe-classify-pagination-change"
            storageKeyBase="CLASSIFY_MFE_PROPS_POSTS_LIMIT"
          />
          <div className={`classification-wall__cards-area${isSelectionLocked ? ' classification-wall__cards-area--locked' : ''}`}>
            <ClassificationCardList posts={posts} stateType={stateType} />
          </div>
          <Pagination
            totalItems={totalItems}
            initialPage={currentPage}
            initialLimit={itemsPerPage}
            eventName="mfe-classify-pagination-change"
            storageKeyBase="CLASSIFY_MFE_PROPS_POSTS_LIMIT"
          />
        </>
      )}
      {shouldShowEmptyState && (
        <ClassificationCardList posts={[]} stateType={stateType} />
      )}

      {isCreateFileOpen && (
        <div className="classification-wall__card-overlay">
          <CreateFileCard
            onClose={() => setIsCreateFileOpen(false)}
            onConfirm={handleCreateFile}
            totalFilteredPosts={totalItems}
          />
        </div>
      )}

      {shouldShowFab && (
        <div className="classification-wall__fab-area">
          <BdsButton size="large" variant="solid" icon="folder" onClick={() => setIsCreateFileOpen(true)}>
            {t('menu.createFile')}
          </BdsButton>
          <BdsButton size="large" variant="solid" icon="notes" onClick={openMultipleEdition}>
            {t('menu.multipleEdition')}
          </BdsButton>
        </div>
      )}

      <MultipleEditionModal isOpen={isMultipleEditionOpen} onClose={closeMultipleEdition} />
      {isMultipleEditionOpen && <div className="classification-wall__modal-backdrop" aria-hidden="true" />}
    </div>
  );
};
